import { fetchWithCache } from '@/firebase/firestore/cache';
import { StorageResolver } from '@/firebase/firestore/storage-resolver';
import { normalizeDates } from '@/lib/date-utils';
import type {
  EntityType,
  Location,
  NovelStorageLayout,
  Relationship
} from '@/lib/types';
import {
  Firestore,
  collection,
  onSnapshot,
  or,
  query,
  where
} from 'firebase/firestore';

export function subscribeEntity<T extends { id: string }>(
  firestore: Firestore,
  ownerId: string,
  novelId: string,
  layout: NovelStorageLayout,
  entityType: EntityType,
  id: string,
  onUpdate: (entity: T | null) => void,
  onError: (error: Error) => void
): () => void {
  const resolver = new StorageResolver(firestore);
  const { ref, fieldPath } = resolver.resolveItem(ownerId, novelId, 'entities', entityType, id, layout);

  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onUpdate(null);
        return;
      }
      const data = snap.data();
      if (!data) {
        onUpdate(null);
        return;
      }
      if (fieldPath) {
        const list = (data as { entityList?: Record<string, T> }).entityList || {};
        const item = list[id] || null;
        onUpdate(item ? normalizeDates({ ...item, id }) : null);
      } else {
        onUpdate(normalizeDates({ ...(data as T), id }));
      }
    },
    onError
  );
}

export async function getEntity<T extends { id: string }>(
  firestore: Firestore,
  ownerId: string,
  novelId: string,
  layout: NovelStorageLayout,
  entityType: EntityType,
  id: string
): Promise<T | null> {
  const resolver = new StorageResolver(firestore);
  const { ref, fieldPath } = resolver.resolveItem(ownerId, novelId, 'entities', entityType, id, layout);

  const snap = await fetchWithCache(ref);
  if (!snap.exists()) {
    return null;
  }
  const data = snap.data();
  if (!data) {
    return null;
  }
  if (fieldPath) {
    const list = (data as { entityList?: Record<string, T> }).entityList || {};
    const item = list[id] || null;
    return item ? normalizeDates({ ...item, id }) : null;
  } else {
    return normalizeDates({ ...(data as T), id });
  }
}

export function subscribeEntities<T extends { id: string }>(
  firestore: Firestore,
  ownerId: string,
  novelId: string,
  layout: NovelStorageLayout,
  entityType: EntityType,
  onUpdate: (entities: T[]) => void,
  onError: (error: Error) => void
): () => void {
  const resolver = new StorageResolver(firestore);
  const mode = layout?.entities?.[entityType]?.items || 'embedded';

  if (mode === 'split') {
    const collRef = collection(resolver.getNovelRef(ownerId, novelId), entityType);
    return onSnapshot(
      collRef,
      (snap) => {
        onUpdate(snap.docs.map(d => normalizeDates({ ...(d.data() as T), id: d.id })));
      },
      onError
    );
  } else {
    const bucketRef = resolver.getBucketRef(ownerId, novelId, 'entities', entityType);
    return onSnapshot(
      bucketRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const list = (data as { entityList?: Record<string, T> } | undefined)?.entityList || {};
          onUpdate(Object.entries(list).map(([id, item]) => normalizeDates({
            ...item,
            id: item.id || id,
          })));
        } else {
          onUpdate([]);
        }
      },
      onError
    );
  }
}

export function subscribeAllRelationships(
  firestore: Firestore,
  ownerId: string,
  novelId: string,
  layout: NovelStorageLayout,
  onUpdate: (relationships: Relationship[]) => void,
  onError: (error: Error) => void
): () => void {
  return subscribeEntities<Relationship>(firestore, ownerId, novelId, layout, 'relationships' as EntityType, onUpdate, onError);
}

export function subscribeRelationships(
  firestore: Firestore,
  ownerId: string,
  novelId: string,
  layout: NovelStorageLayout,
  entityId: string,
  onUpdate: (relationships: Relationship[]) => void,
  onError: (error: Error) => void
): () => void {
  const resolver = new StorageResolver(firestore);
  const mode = layout?.entities?.relationships?.items || 'embedded';

  if (mode === 'split') {
    const collRef = collection(resolver.getNovelRef(ownerId, novelId), 'relationships');
    const q = query(
      collRef,
      or(
        where('entityAId', '==', entityId),
        where('entityBId', '==', entityId)
      )
    );
    return onSnapshot(
      q,
      (snap) => {
        onUpdate(snap.docs.map(d => normalizeDates({ ...(d.data() as Relationship), id: d.id })));
      },
      onError
    );
  } else {
    const bucketRef = resolver.getBucketRef(ownerId, novelId, 'entities', 'relationships');
    return onSnapshot(
      bucketRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const list = (data as { entityList?: Record<string, Relationship> } | undefined)?.entityList || {};
          const filtered = Object.values(list)
            .map(item => normalizeDates({ ...item }))
            .filter(r => r.entityAId === entityId || r.entityBId === entityId);
          onUpdate(filtered);
        } else {
          onUpdate([]);
        }
      },
      onError
    );
  }
}

export function subscribeChildLocations(
  firestore: Firestore,
  ownerId: string,
  novelId: string,
  layout: NovelStorageLayout,
  parentLocationId: string,
  onUpdate: (locations: Location[]) => void,
  onError: (error: Error) => void
): () => void {
  const resolver = new StorageResolver(firestore);
  const mode = layout?.entities?.locations?.items || 'embedded';

  if (mode === 'split') {
    const collRef = collection(resolver.getNovelRef(ownerId, novelId), 'locations');
    const q = query(collRef, where('parentId', '==', parentLocationId));
    return onSnapshot(
      q,
      (snap) => {
        onUpdate(snap.docs.map(d => normalizeDates({ ...(d.data() as Location), id: d.id })));
      },
      onError
    );
  } else {
    const bucketRef = resolver.getBucketRef(ownerId, novelId, 'entities', 'locations');
    return onSnapshot(
      bucketRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const list = (data as { entityList?: Record<string, Location> } | undefined)?.entityList || {};
          const filtered = Object.values(list)
            .map(item => normalizeDates({ ...item }))
            .filter(loc => loc.parentId === parentLocationId);
          onUpdate(filtered);
        } else {
          onUpdate([]);
        }
      },
      onError
    );
  }
}
